import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Form,
  Label,
  Input,
  Button,
  Spinner,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import CommonDataTable from "../../../Components/Common/Table/CommonDataTable";
import usePermissions from "../../../Components/Hooks/usePermissions";
import { fetchChatReport, fetchHumanServiceChats, fetchChatReportTableColumnNames, fetchClientUsers, fetchHumanCategoriesAll, updateUserPreference } from "../../../slices/thunks";
import { useDispatch, useSelector } from "react-redux";
import ColumnSelectionModal from "../../../Components/Common/ColumnSelectionModal/ColumnSelectionModal";

// --- New: Import react-select ---
import Select from "react-select";
import i18next from "i18next";
import ChatHistoryModal from "../../../Components/Common/ChatHistoryModal";



const ChatReport = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [searchTerm, setSearchTerm] = useState("");
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedColumns, setSelectedColumns] = useState([]);
    const [historyModalOpen, setHistoryModalOpen] = useState(false);
    const [selectedSession, setSelectedSession] = useState(null); 
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [status, setStatus] = useState("");
    const [category, setCategory] = useState(""); // This will map to ServiceID
    const today = new Date().toISOString().split("T")[0];

    const actionConfig = {
        showCheckboxColumn: false,
        action: false
    };

    const permission = (() => {
         return usePermissions(1, 13);
    })();

    const { chatSession, loading, chats, tableColumnNames, humanCategories, clientUsers, clientUsersloading, categoryloading, chatloading } = useSelector(
        (state) => ({
            tableColumnNames: state.ChatReport.tableColumnNames,
            loading: state.ChatReport.loading,
            chats: state.ChatReport.chats,
            chatSession: state.ChatReport.chatSession,
            humanCategories: state.ChatReport.humanCategories,
            clientUsers: state.ChatReport.clientUsers,
            clientUsersloading: state.ChatReport.clientUsersloading,
            categoryloading: state.ChatReport.categoryloading,
            chatloading: state.ChatReport.chatloading,
        })
    );

    const handleSearch = (e) => {
        e.preventDefault();

        const payload = {};
        if (selectedCustomer) payload.UserID = selectedCustomer.value;
        if (fromDate) payload.FromDate = fromDate;
        if (toDate) payload.ToDate = toDate;
        if (status) payload.Status = status;
        if (category) payload.ServiceID = category;

        dispatch(fetchChatReport(payload));
    };

    const handleClear = (e) => {
        e.preventDefault();
        setSelectedCustomer(null); 
        setFromDate("");
        setToDate("");
        setStatus("");
        setCategory("");
        dispatch(fetchChatReport());
    };

    const handleViewHistory = (session) => {
        dispatch(fetchHumanServiceChats({SessionID: session.SessionID}))
        setSelectedSession(session);
        setHistoryModalOpen(true);
    };

    const toggleHistoryModal = () => {
        setHistoryModalOpen(false);
        setSelectedSession(null); // Clear data when closing
    };

    
    const saveUserPreferences = async () => {
        const preferencesPayload = {
        ColumnIDs: tableColumnNames.reduce((acc, column) => {
            acc[column.ID] = selectedColumns.includes(column.ID);
            return acc;
        }, {}),
        };

        setModalOpen(false);
        try {
        await dispatch(updateUserPreference(preferencesPayload)).unwrap();
        await dispatch(
            fetchChatReportTableColumnNames({
            module: "CHAT_REPORT",
            })
        );

        } catch (error) {
        console.error("Error updating preferences:", error);
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                dispatch(fetchChatReport()); 
                dispatch(fetchChatReportTableColumnNames({ module: "CHAT_REPORT" }));
                dispatch(fetchHumanCategoriesAll()); 
                dispatch(fetchClientUsers({ IsSystemUser: false, IsActive: true }));
            } catch (error) {
                console.error(error);
            }   
        };

        fetchData();
    }, [dispatch]);

    document.title = "Alitqan | Human Chat Report";

    return (
        <React.Fragment>
        <div className="page-content">
            <Container fluid={true}>
            <Row className="mb-3">
                <Col>
                    <BreadCrumb title={t("ChatReport")} />
                </Col>
            </Row>

            <Row>
                <Col lg={12}>
                <Card>
                    <CardHeader>
                    <h5 className="card-title mb-0">{t("SearchFilters")}</h5>
                    </CardHeader>
                    <CardBody>
                    <Form onSubmit={handleSearch}>
                        <Row className="g-3">
                        <Col md={3}>
                            <Label for="customer-search">{t("Customer")}</Label>
                            {clientUsersloading ? (
                                <div className="d-flex align-items-center" style={{ minHeight: 38 }}>
                                    <Spinner size="sm" color="primary" className="me-2" />
                                    {t("Loading...")}
                                </div>
                            ) : (
                                <Select
                                    id="customer-search"
                                    name="customer"
                                    options={clientUsers.map(u => ({
                                        value: u.ID || u.Id,
                                        label: u.UserName || u.Email || u.Phone
                                    }))}
                                    value={selectedCustomer}
                                    onChange={(selectedOption) => setSelectedCustomer(selectedOption)}
                                    placeholder={t("SearchAndSelectCustomer")}
                                    isClearable={true}
                                    isSearchable={true}
                                    classNamePrefix="select2-selection"
                                />
                            )}
                        </Col>

                        <Col md={4}>
                            <Label>{t("DateLastMessage")}</Label>
                            <div className="d-flex gap-2">
                                {/* // --- Modified: Added value and onChange --- */}
                                <Input type="date" name="dateFrom" value={fromDate} onChange={(e) => setFromDate(e.target.value)} max={today} />
                                <Input type="date" name="dateTo" value={toDate} onChange={(e) => setToDate(e.target.value)} max={today} />
                            </div>
                        </Col>

                        <Col md={2}>
                            <Label for="status-filter">{t("Status")}</Label>
                            {/* // --- Modified: Added value and onChange --- */}
                            <Input
                                type="select"
                                id="status-filter"
                                name="status"
                                value={status}
                                onChange={(e) => setStatus(e.target.value)}
                            >
                                <option value="">{t("AllStatus")}</option>
                                <option value="1">{t("ChatReportLableNew")}</option>
                                <option value="2">{t("ChatReportLableInProgress")}</option>
                                <option value="3">{t("ChatReportLableOnHold")}</option>
                                <option value="4">{t("ChatReportLableComplete")}</option>
                            </Input>
                        </Col>

                        <Col md={3}>
                            <Label for="category-filter">{t("Category")}</Label>
                             {categoryloading ? (
                                <div className="d-flex align-items-center" style={{ minHeight: 38 }}>
                                    <Spinner size="sm" color="primary" className="me-2" />
                                    {t("Loading...")}
                                </div>
                                ) : (
                                    <Input
                                        type="select"
                                        id="category-filter"
                                        name="category"
                                        value={category}
                                        onChange={(e) => setCategory(e.target.value)}
                                    >
                                        <option value="">{t("AllCategory")}</option>
                                        {humanCategories.map((category) => (
                                            <option key={category.ID} value={category.ID}>
                                                {i18next.language === "en" ? category.TitleEn : category.TitleAr}
                                            </option>
                                        ))}
                                    </Input>
                                )}
                        </Col>
                        
                        <Col md={12} className="text-end ">
                            {/* // --- Modified: Added onClick handlers --- */}
                            <Button type="submit" color="primary" className="me-2">
                                <i className="ri-search-line align-bottom me-1"></i> {t("Search")}
                            </Button>
                            <Button color="light" className="me-2" onClick={handleClear}>
                                <i className="ri-close-circle-line align-bottom me-1"></i> {t("Clear")}
                            </Button>
                        </Col>
                        </Row>
                    </Form>
                    </CardBody>
                </Card>
                </Col>
            </Row>

            <div className="table-responsive">
                <Card>
                    <CardHeader>
                        <Row className="g-4 ">
                            <Col className="col-sm-auto">
                            <div>
                                <h5 className="card-title mb-0">{t("ChatSession")}</h5>
                            </div>
                            </Col>
                            <Col className="col-sm">
                            <div className="d-flex justify-content-sm-end">
                                <Button
                                    className="btn btn-soft"
                                    onClick={() => setModalOpen(true)}
                                    disabled={loading}
                                >
                                <i className="ri-filter-3-line"></i>
                                </Button>
                                <div className="search-box ms-2 me-2">
                                <Input
                                    type="text"
                                    className="form-control search"
                                    placeholder={t("Search here")}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                                <i className="ri-search-line search-icon"></i>
                                </div>
                            </div>
                            </Col>
                        </Row>
                    </CardHeader>
                    <CardBody>
                        {loading ? (
                        <div className="d-flex justify-content-center align-items-center py-5">
                            <Spinner style={{ height: "3rem", width: "3rem" }} className="me-2">
                            Loading...
                            </Spinner>
                        </div>
                        ) : (
                        <div className="listjs-table" id="customerList">
                            <CommonDataTable
                            data={chatSession}
                            tableColumnNames={tableColumnNames}
                            searchTerm={searchTerm}
                            actionConfig={actionConfig}
                            permission={permission}
                            onViewHistory={handleViewHistory}
                            />
                        </div>
                        )}
                    </CardBody>
                </Card>
            </div>
            </Container>

            <ColumnSelectionModal
                isOpen={modalOpen}
                toggle={() => setModalOpen(!modalOpen)}
                columns={tableColumnNames}
                selectedColumns={selectedColumns}
                setSelectedColumns={setSelectedColumns}
                handleSave={saveUserPreferences}
            />

            {/* It will only be visible when historyModalOpen is true */}
            <ChatHistoryModal
                isOpen={historyModalOpen}
                toggle={toggleHistoryModal}
                session={selectedSession || {}}
                loading={chatloading}
                messages={chats}
                chatType="human"
            />
        </div>
        </React.Fragment>
    );
};

export default ChatReport;